import { useEffect } from 'react'
import useNav from '../../../../hooks/useNav'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Breadcrumb } from '../../../../components/ui/breadcrumb';
import { styles } from '../../../../styles/tailwind-config';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../components/ui/table';
import { Eye } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../../context/AuthProvider';
import { Skeleton } from '../../../../components/ui/skeleton';
import { Trans, useLingui } from '@lingui/react/macro'
import axios from '../../../../lib/axios';

type PreviousPage = 'startpage' | 'intervention'
type VisitRapportProps = { previousPage?: PreviousPage }

// New: reflect the paged response shape
type InterventionHistoryResponse = {
  page: number
  totalrecords: number
  totalpages: number
  interventions: InterventionHistory[]
}

const VisitRapport = ({ previousPage='startpage' }: VisitRapportProps) => {
  const { auth } = useAuth()
  const { t } = useLingui()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { setNav, nav } = useNav()

  const interventionId = searchParams.get('interventionid') || ''
  const dbid = auth.data?.databases?.[0]?.dbid

  useEffect(() => {
    setNav({ ...nav, title: t`Bezoekrapport - interventionid ${interventionId}` })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { data: interventionHistory, isLoading } = useQuery<
    InterventionHistory | undefined,
    Error,
    InterventionHistory | undefined
  >({
    queryKey: ['intervention-history-rapport', { dbid, interventionId }],
    queryFn: async () => {
      const resp = await axios.get<InterventionHistoryResponse>(
        `intervention/history?dbid=${dbid}&interventionid=${interventionId}&page=1&limit=1`
      )
      // New: unwrap the paged response
      return resp.data?.interventions?.[0]
    },
    enabled: !!dbid && !!interventionId,
    placeholderData: (prev) => prev,
    gcTime: 5 * 60 * 1000,
  })

  return (
    <div className='flex flex-col gap-4 p-4'>
      {previousPage === 'startpage' && (
        <Breadcrumb items={[
          { title: <Link to="/"><Trans>Startpagina</Trans></Link> },
          { title: <Link to=""><Trans>Bezoekrapport</Trans></Link> }
        ]}/>
      )}
      {previousPage === 'intervention' && (
        <Breadcrumb items={[
          { title: <button onClick={()=>{navigate(-1)}}>Interventie {interventionId}</button> },
          { title: <Link to=""><Trans>Bezoekrapport</Trans></Link> }
        ]}/>
      )}

      <h1 className={`${styles.h1}`}><Trans>Bezoekrapport</Trans></h1>

      {(!interventionHistory && isLoading) && (
        <>
          {Array(5).fill('').map((_, i) => (<Skeleton key={`skeleton-${i}`} className='w-full h-[250px]'/>))}
        </>
      )}

      {interventionHistory && (
        <>
          <div className='flex flex-col gap-2 p-4 border rounded-md'>
            <h3 className={styles.h3}><Trans>Korte beschrijving van de interventie</Trans></h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead><Trans>Externe ID</Trans></TableHead>
                  <TableHead><Trans>Interne ID</Trans></TableHead>
                  <TableHead><Trans>Merk</Trans></TableHead>
                  <TableHead><Trans>Type</Trans></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {interventionHistory.m_aMACHINES.map((machine) => (
                  <TableRow key={machine.m_iMACHINEID}>
                    <TableCell>{machine.m_sEXTNUMMER}</TableCell>
                    <TableCell>{machine.m_sINTNUMMER}</TableCell>
                    <TableCell>{machine.m_sMERK}</TableCell>
                    <TableCell>{machine.m_sTYPEDESCRIPTION || machine.m_sTYPE || t`Geen type`}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div>
              <p className='text-sm font-medium text-neutral-500'><Trans>Reden</Trans></p>
              <p className='text-sm font-medium text-neutral-800'>{interventionHistory?.m_sOMSCHRIJVING || t`Geen reden`}</p>
            </div>
            <div>
              <p className='text-sm font-medium text-neutral-500'><Trans>Oplossing</Trans></p>
              <p className='text-sm font-medium text-neutral-800'>
                {interventionHistory?.m_sOPLOSSING} {interventionHistory?.m_sOPLOSSINGEXTRA}
                {(!interventionHistory?.m_sOPLOSSING && !interventionHistory?.m_sOPLOSSINGEXTRA) && 'Geen oplossing'}
              </p>
            </div>
          </div>

        <div className='flex-col hidden gap-2 p-4 border rounded-md'>
            <h3 className={styles.h3}><Trans>Gebruikte wisselstukken</Trans></h3>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead><Trans>Beschrijving</Trans></TableHead>
                        <TableHead><Trans>Aantal</Trans></TableHead>
                        <TableHead><Trans>BOM</Trans></TableHead>
                        <TableHead><Trans>PARTID</Trans></TableHead>
                        <TableHead><Trans>PARTID LEV</Trans></TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {Array(4).fill('').map((_,i)=>(
                    <TableRow key={i}>
                        <TableCell>TODO</TableCell>
                        <TableCell>TODO</TableCell>
                        <TableCell>TODO</TableCell>
                        <TableCell>TODO</TableCell>
                        <TableCell>TODO</TableCell>
                    </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>

        <div className='flex flex-col gap-2 p-4 border rounded-md'>
            <h3 className={styles.h3}><Trans>Gepresteerde tijd</Trans></h3>
            <div>
                <p className='text-sm font-medium text-neutral-500'><Trans>Activiteit</Trans></p>
                <p className='text-sm font-medium text-neutral-800'>Andere</p>
            </div>
            <div>
                <p className='text-sm font-medium text-neutral-500'><Trans>Start bezoek</Trans></p>
                <p className='text-sm font-medium text-neutral-800'>{interventionHistory?.m_oFACTUREERDETIJD?.m_tTMVAN || '-'}</p>
            </div>
            <div>
                <p className='text-sm font-medium text-neutral-500'><Trans>Einde bezoek</Trans></p>
                <p className='text-sm font-medium text-neutral-800'>{interventionHistory?.m_oFACTUREERDETIJD?.m_tTMTOT || '-'}</p>
            </div>
            <div>
                <p className='text-sm font-medium text-neutral-500'><Trans>Duur bezoek</Trans></p>
                <p className='text-sm font-medium text-neutral-800'>{interventionHistory?.m_oFACTUREERDETIJD?.m_tDURATION || '-'}</p>
            </div>
            <div>
                <p className='text-sm font-medium text-neutral-500'><Trans>Aantal verplaatsingen</Trans></p>
                <p className='text-sm font-medium text-neutral-800'>{interventionHistory?.m_oFACTUREERDETIJD?.m_iAANTALVERPLAATSING || '0'}</p>
            </div>
        </div>

        <div className='flex flex-col gap-2 p-4 border rounded-md'>
            <div className='flex items-center justify-between'>
                <h3 className={styles.h3}><Trans>Documenten</Trans></h3>
                {/* <Link to={`vragenlijst?callid=${searchParams.get('callid')}`} className='text-xs text-orange-600 underline w-fit'>Vragenlijst controleren</Link> */}
            </div>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead><Trans>Documentnaam</Trans></TableHead>
                        <TableHead><Trans>Toegevoegd op</Trans></TableHead>
                        <TableHead></TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {Array(4).fill('').map((_,i)=>(
                    <TableRow key={i}>
                        <TableCell>TODO</TableCell>
                        <TableCell>TODO</TableCell>
                        <TableCell>
                            <div className='flex items-center justify-end w-full'><Eye className='w-4 h-4 text-primary' strokeWidth={1}/></div>
                        </TableCell>
                    </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>

        <div className='flex flex-col gap-2 p-4 border rounded-md'>
            <h3 className={styles.h3}><Trans>Foto's</Trans></h3>
            <div>
                <p>TODO</p>
                <p className='text-sm font-medium text-neutral-500'><Trans>Geen foto's gemaakt tijdens deze interventie</Trans></p>
            </div>
        </div>
        </>
        
         )}

      <Button onClick={()=>{navigate(-1)}} variant='secondary'><Trans>Terug</Trans></Button>
    </div>
  )
}

export default VisitRapport